import { classifyLinkImpairment, FRAME_EVIDENCE_STALE_MS } from '@playcastdotio/PeerCore';

export const HOST_WINDOW_TRANSITION_HOLD_MS = 4_000;

const WINDOW_STATES = new Set(['minimized', 'normal', 'restored', 'maximized']);

export interface HostWindowTransitionEvidence {
  sampledAtMs: number;
  packetsLostPct?: number;
  standingQueueMs?: number | null;
  rttMs?: number | null;
  targetBitrateBps?: number;
  sentVideoBitrateBps?: number;
  qualityLimitationReason?: string | null;
  sourceCadence?: {
    sampledAtMs: number;
    intervalMs: number;
    framesDelta: number;
  };
}

export interface WindowTransitionDecision {
  transitionActive: boolean;
  cadenceRecovered: boolean;
  permitHardCut: boolean;
  releaseTransitionCut: boolean;
  reason: string;
}

/** Pure, freshness-aware policy for host minimize/restore/maximize stalls. */
export class HostWindowTransitionGuard {
  private transitionAtMs: number | undefined;

  public note(nowMs: number): void {
    if (!Number.isFinite(nowMs)) return;
    this.transitionAtMs = nowMs;
  }

  /** The most recent transition timestamp, for an episode to stamp durably at entry. */
  public currentTransitionAtMs(): number | undefined {
    return this.transitionAtMs;
  }

  public decide(
    nowMs: number,
    evidence: HostWindowTransitionEvidence | undefined,
    /** The episode's durable association: the transition it was entered during, if any. */
    episodeTransitionAtMs?: number,
  ): WindowTransitionDecision {
    const transitionAtMs = episodeTransitionAtMs ?? this.transitionAtMs;
    const transitionActive = transitionAtMs !== undefined
      && nowMs >= transitionAtMs
      && nowMs - transitionAtMs < HOST_WINDOW_TRANSITION_HOLD_MS;
    const evidenceFresh = this.fresh(nowMs, evidence?.sampledAtMs);
    const cadence = evidence?.sourceCadence;
    const cadenceFresh = this.fresh(nowMs, cadence?.sampledAtMs);
    const cadenceFps = cadenceFresh && cadence && cadence.intervalMs > 0
      ? cadence.framesDelta * 1_000 / cadence.intervalMs
      : undefined;
    const cadenceRecovered = cadenceFps !== undefined && Number.isFinite(cadenceFps) && cadenceFps >= 20;
    const hasTransport = evidenceFresh && evidence !== undefined && [
      evidence.packetsLostPct, evidence.standingQueueMs, evidence.rttMs,
    ].some(value => typeof value === 'number' && Number.isFinite(value));
    const impairment = hasTransport && evidence
      ? classifyLinkImpairment({
          packetsLostPct: evidence.packetsLostPct,
          standingQueueMs: evidence.standingQueueMs,
          rttMs: evidence.rttMs,
        })
      : undefined;
    const capacityCollapsed = evidenceFresh && evidence !== undefined
      && this.capacityCollapsed(evidence);
    const permitHardCut = impairment === 'congestion' || capacityCollapsed;
    // Durable association: the episode stamps the transition it was entered during, so a
    // minimize at T1 and a restore more than a second later still associate — even though
    // note() has since overwritten the guard's own timestamp.
    const associated = transitionAtMs !== undefined && episodeTransitionAtMs !== undefined;
    const releaseTransitionCut = associated && cadenceRecovered && hasTransport && !permitHardCut;

    let reason = 'no-window-transition';
    if (transitionActive && !evidenceFresh) reason = 'awaiting-fresh-transport';
    else if (transitionActive && !cadenceRecovered) reason = 'awaiting-capture-cadence';
    else if (capacityCollapsed) reason = 'fresh-capacity-collapse';
    else if (impairment === 'congestion') reason = 'fresh-link-congestion';
    else if (releaseTransitionCut) reason = 'fresh-transition-recovery';
    else if (transitionActive) reason = 'window-transition-hold';

    return { transitionActive, cadenceRecovered, permitHardCut, releaseTransitionCut, reason };
  }

  public clear(): void {
    this.transitionAtMs = undefined;
  }

  private fresh(nowMs: number, sampledAtMs: number | undefined): boolean {
    return sampledAtMs !== undefined && Number.isFinite(nowMs) && Number.isFinite(sampledAtMs)
      && nowMs >= sampledAtMs && nowMs - sampledAtMs < FRAME_EVIDENCE_STALE_MS;
  }

  private capacityCollapsed(evidence: HostWindowTransitionEvidence): boolean {
    const sent = evidence.sentVideoBitrateBps;
    const target = evidence.targetBitrateBps;
    return evidence.qualityLimitationReason === 'bandwidth'
      && typeof sent === 'number' && Number.isFinite(sent) && sent >= 0
      && typeof target === 'number' && Number.isFinite(target) && target > 0
      && sent < target * 0.5;
  }
}

type WindowEventTarget = {
  on?: (channel: string, callback: (state: string) => void) => void;
  removeListener?: (channel: string, callback: (state: string) => void) => void;
};

export function watchHostWindowTransitions(
  onTransition: () => void,
  electron: WindowEventTarget | undefined = (globalThis as { electron?: WindowEventTarget }).electron,
): () => void {
  if (!electron?.on) return () => undefined;
  const handler = (state: string) => {
    if (WINDOW_STATES.has(state)) onTransition();
  };
  electron.on('window-state-changed', handler);
  return () => electron.removeListener?.('window-state-changed', handler);
}
